import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { prepareEmpireFlagFile } from '../lib/empireFlagImage';
import { changeAppLanguage } from '../i18n/changeLanguage';
import { translateApiError } from '../i18n/errors';
import { isAppLang, LANG_LABELS, SUPPORTED_LANGS, type AppLang } from '../i18n/locale';
import { useApp } from '../store';
import { MobileSheetHandle, useMobileSheetExpand } from './MobileSheet';

function currentAppLang(lng: string | undefined): AppLang {
  const code = lng?.split('-')[0];
  return isAppLang(code) ? code : 'fr';
}

export function ProfilePanel() {
  const { t, i18n } = useTranslation(['panels', 'common']);
  const { t: tError } = useTranslation('errors');
  const { user, setUser, setWallet, setZones, setPanel, bumpMapPaint, setTutorialOpen, setNewsOpen } = useApp();
  const { className: sheetExpandClassName } = useMobileSheetExpand('sm');
  const [name, setName] = useState(user?.empireName || '');
  const [color, setColor] = useState(user?.empireColor || '#3D8BFF');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagError, setFlagError] = useState<string | null>(null);
  const flagInputRef = useRef<HTMLInputElement>(null);
  const firstTime = !!user?.needsEmpireName;

  const [pwOpen, setPwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwOk, setPwOk] = useState<string | null>(null);
  const [pwBusy, setPwBusy] = useState(false);

  const [roboUnlocked, setRoboUnlocked] = useState(false);
  const [roboEnabled, setRoboEnabled] = useState(false);
  const [roboBusy, setRoboBusy] = useState(false);

  const [langBusy, setLangBusy] = useState(false);
  const appLang = currentAppLang(i18n.language);

  useEffect(() => {
    let alive = true;
    void api.me().then((res) => {
      if (!alive || !res.robotization) return;
      setRoboUnlocked(!!res.robotization.unlocked);
      setRoboEnabled(!!res.robotization.enabled);
    }).catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [user?.id]);

  if (!user) return null;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const colorChanged = color.toUpperCase() !== (user?.empireColor || '').toUpperCase();
      const res = firstTime
        ? await api.setEmpire(name, color)
        : await api.updateProfile({ empireName: name, empireColor: color });
      setUser(res.user);
      if (colorChanged || firstTime) bumpMapPaint();
      setPanel('none');
    } catch (err) {
      setError(translateApiError(err, tError));
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwBusy(true);
    setPwError(null);
    setPwOk(null);
    try {
      await api.changePassword(currentPassword, newPassword, newPasswordConfirm);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setPwOk(t('profile.passwordUpdated'));
    } catch (err) {
      setPwError(translateApiError(err, tError));
    } finally {
      setPwBusy(false);
    }
  }

  async function logout() {
    await api.logout();
    setUser(null);
    setWallet(null);
    setZones(null);
    setPanel('none');
  }

  const inputClass =
    'mt-1 w-full rounded-xl border border-[var(--line)] bg-[#0b1219] px-3 py-2 text-sm outline-none focus:border-[var(--accent)]';

  return (
    <aside className={`absolute inset-x-0 bottom-0 z-30 max-h-[85vh] overflow-auto rounded-t-3xl border border-[var(--line)] bg-[var(--bg-panel)] p-5 shadow-2xl backdrop-blur-xl sm:inset-x-auto sm:right-4 sm:top-24 sm:bottom-auto sm:w-[22rem] sm:rounded-3xl ${sheetExpandClassName}`}>
      <MobileSheetHandle />
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="font-[family-name:var(--font-display)] text-xl text-[var(--accent)]">
            {firstTime ? t('profile.firstTimeTitle') : t('profile.title')}
          </h2>
          <p className="text-xs text-[var(--muted)]">
            {firstTime
              ? t('profile.firstTimeHint')
              : t('profile.connectedAs', { username: user.username })}
          </p>
        </div>
        {!firstTime && (
          <button data-panel-close type="button" className="text-[var(--muted)]" onClick={() => setPanel('none')}>
            {t('common:close')}
          </button>
        )}
      </div>

      {!firstTime && (
        <button
          type="button"
          onClick={() => setPanel('shortcuts')}
          className="mb-4 w-full rounded-xl border border-[var(--accent)]/35 bg-[var(--accent)]/[0.07] px-4 py-2.5 text-left text-sm text-[var(--ink)] transition hover:border-[var(--accent)] hover:bg-[var(--accent)]/12"
        >
          <span className="block font-semibold text-[var(--accent)]">{t('profile.shortcutsTitle')}</span>
          <span className="mt-0.5 block text-[11px] leading-snug text-[var(--muted)]">
            {t('profile.shortcutsHint')}
          </span>
        </button>
      )}

      <div className="mb-4 rounded-xl border border-[var(--line)] bg-[#0b1219]/50 p-3">
        <label className="text-xs text-[var(--muted)]">
          {t('profile.language')}
          <p className="mt-0.5 text-[10px] leading-snug">{t('profile.languageHint')}</p>
          <select
            className={`${inputClass} mt-2 cursor-pointer`}
            value={appLang}
            disabled={langBusy}
            onChange={(e) => {
              const next = e.target.value;
              if (!isAppLang(next) || next === appLang) return;
              setLangBusy(true);
              void changeAppLanguage(next).finally(() => setLangBusy(false));
            }}
          >
            {SUPPORTED_LANGS.map((lang) => (
              <option key={lang} value={lang}>
                {LANG_LABELS[lang]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <form className="flex flex-col gap-3" onSubmit={save}>
        <label className="text-xs text-[var(--muted)]">
          {t('profile.empireName')}
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            maxLength={80}
          />
        </label>
        <label className="text-xs text-[var(--muted)]">
          {t('profile.empireColor')}
          <div className="mt-1 flex gap-2">
            <input
              type="color"
              className="h-10 w-14 cursor-pointer rounded-lg border border-[var(--line)] bg-transparent"
              value={color}
              onChange={(e) => setColor(e.target.value.toUpperCase())}
            />
            <input
              className="flex-1 rounded-xl border border-[var(--line)] bg-[#0b1219] px-3 py-2 text-sm uppercase outline-none focus:border-[var(--accent)]"
              value={color}
              onChange={(e) => setColor(e.target.value.toUpperCase())}
              pattern="#[0-9A-Fa-f]{6}"
              required
            />
          </div>
        </label>
        {!firstTime && (
          <div className="rounded-xl border border-[var(--line)] bg-[#0b1219]/50 p-3">
            <div className="flex items-start gap-3">
              <div className="relative h-12 w-[4.5rem] shrink-0 overflow-hidden rounded-md border border-[var(--line)] bg-[#141c28]">
                {user.empireFlagUrl ? (
                  <img
                    key={user.empireFlagUrl}
                    src={user.empireFlagUrl}
                    alt={t('profile.flagAlt')}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span
                    className="block h-full w-full"
                    style={{ background: user.empireColor }}
                    title={t('profile.flagNone')}
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-[var(--ink)]">{t('profile.flagTitle')}</p>
                <p className="mt-0.5 text-[10px] leading-snug text-[var(--muted)]">
                  {t('profile.flagHint')}
                </p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    disabled={flagBusy}
                    onClick={() => flagInputRef.current?.click()}
                    className="rounded-lg border border-[var(--line)] px-2 py-1 text-[11px] font-semibold text-[var(--accent)] hover:border-[var(--accent)] disabled:opacity-50"
                  >
                    {flagBusy ? t('common:loadingShort') : user.empireFlagUrl ? t('profile.change') : t('profile.upload')}
                  </button>
                  {user.empireFlagUrl && (
                    <button
                      type="button"
                      disabled={flagBusy}
                      onClick={() => {
                        setFlagBusy(true);
                        setFlagError(null);
                        void api
                          .removeEmpireFlag()
                          .then((res) => {
                            setUser(res.user);
                            bumpMapPaint();
                          })
                          .catch((err) =>
                            setFlagError(translateApiError(err, tError)),
                          )
                          .finally(() => setFlagBusy(false));
                      }}
                      className="rounded-lg border border-[var(--danger)]/40 px-2 py-1 text-[11px] text-[var(--danger)] disabled:opacity-50"
                    >
                      {t('profile.remove')}
                    </button>
                  )}
                </div>
                <input
                  ref={flagInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (!file) return;
                    setFlagBusy(true);
                    setFlagError(null);
                    void prepareEmpireFlagFile(file)
                      .then((prepared) => api.uploadEmpireFlag(prepared))
                      .then((res) => {
                        setUser(res.user);
                        bumpMapPaint();
                      })
                      .catch((err) =>
                        setFlagError(translateApiError(err, tError)),
                      )
                      .finally(() => setFlagBusy(false));
                  }}
                />
                {flagError && <p className="mt-1 text-[11px] text-[var(--danger)]">{flagError}</p>}
              </div>
            </div>
          </div>
        )}
        {error && <p className="text-sm text-[var(--danger)]">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          className="rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[#1a1408] disabled:opacity-60"
        >
          {busy ? t('common:loadingShort') : firstTime ? t('profile.enterWorld') : t('common:save')}
        </button>
      </form>

      {!firstTime && (
        <>
          {roboUnlocked && (
            <div className="mt-5 border-t border-[var(--line)] pt-4">
              <h3 className="text-sm font-semibold text-[var(--ink)]">{t('profile.robotization')}</h3>
              <p className="mt-1 text-[11px] leading-relaxed text-[var(--muted)]">
                {t('profile.robotizationHint')}
              </p>
              <label className="mt-3 flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-[var(--line)] bg-[#0b1219]/50 px-3 py-2.5">
                <span className="text-sm text-[var(--ink)]">{t('profile.autoInvest')}</span>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--accent)]"
                  checked={roboEnabled}
                  disabled={roboBusy}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setRoboBusy(true);
                    void api
                      .setRobotization(next)
                      .then((res) => {
                        if (res.ok && res.robotization) {
                          setRoboEnabled(res.robotization.enabled);
                        } else {
                          alert(res.error || t('profile.robotizationFail'));
                        }
                      })
                      .catch((err) => alert(translateApiError(err, tError)))
                      .finally(() => setRoboBusy(false));
                  }}
                />
              </label>
            </div>
          )}

          <div className="mt-5 border-t border-[var(--line)] pt-4">
            {!pwOpen ? (
              <button
                type="button"
                onClick={() => {
                  setPwOpen(true);
                  setPwError(null);
                  setPwOk(null);
                }}
                className="w-full rounded-xl border border-[var(--line)] px-4 py-2.5 text-left text-sm font-semibold text-[var(--ink)] transition hover:border-[var(--accent)] hover:text-[var(--accent)]"
              >
                {t('profile.changePassword')}
              </button>
            ) : (
              <form className="flex flex-col gap-3" onSubmit={(e) => void changePassword(e)}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-[var(--ink)]">{t('profile.passwordSection')}</h3>
                  <button
                    type="button"
                    className="text-xs text-[var(--muted)] hover:text-[var(--ink)]"
                    onClick={() => {
                      setPwOpen(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setNewPasswordConfirm('');
                      setPwError(null);
                    }}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
                <label className="text-xs text-[var(--muted)]">
                  {t('profile.currentPassword')}
                  <input
                    type="password"
                    autoComplete="current-password"
                    className={inputClass}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    minLength={4}
                  />
                </label>
                <label className="text-xs text-[var(--muted)]">
                  {t('profile.newPassword')}
                  <input
                    type="password"
                    autoComplete="new-password"
                    className={inputClass}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={4}
                  />
                </label>
                <label className="text-xs text-[var(--muted)]">
                  {t('profile.confirmPassword')}
                  <input
                    type="password"
                    autoComplete="new-password"
                    className={inputClass}
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    required
                    minLength={4}
                  />
                </label>
                {pwError && <p className="text-sm text-[var(--danger)]">{pwError}</p>}
                {pwOk && <p className="text-sm text-[var(--ok)]">{pwOk}</p>}
                <button
                  type="submit"
                  disabled={pwBusy}
                  className="rounded-xl border border-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-[var(--accent)] disabled:opacity-60"
                >
                  {pwBusy ? t('common:loadingShort') : t('common:save')}
                </button>
              </form>
            )}
          </div>

          <button
            type="button"
            onClick={() => setPanel('suggestions')}
            className="mt-4 w-full rounded-xl border border-[var(--accent)]/35 bg-[var(--accent)]/[0.07] px-4 py-2.5 text-left text-sm text-[var(--ink)] transition hover:border-[var(--accent)] hover:bg-[var(--accent)]/12"
          >
            <span className="block font-semibold text-[var(--accent)]">{t('profile.ideasBox')}</span>
            <span className="mt-0.5 block text-[11px] leading-snug text-[var(--muted)]">
              {user.isAdmin ? t('profile.ideasAdmin') : t('profile.ideasPlayer')}
            </span>
          </button>
          <button
            type="button"
            onClick={() => {
              setPanel('none');
              setNewsOpen(true);
            }}
            className="mt-2 w-full rounded-xl border border-[var(--line)] px-4 py-2 text-sm text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--ink)]"
          >
            {t('profile.news')}
          </button>
          <button
            type="button"
            onClick={() => {
              setPanel('none');
              setTutorialOpen(true);
            }}
            className="mt-2 w-full rounded-xl border border-[var(--line)] px-4 py-2 text-sm text-[var(--muted)] hover:border-[var(--accent)] hover:text-[var(--ink)]"
          >
            {t('profile.reviewGuide')}
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="mt-2 w-full rounded-xl border border-[var(--line)] px-4 py-2 text-sm text-[var(--muted)]"
          >
            {t('profile.logout')}
          </button>
        </>
      )}
    </aside>
  );
}
